import React, { useState, useEffect, useRef } from 'react';
import { Button, Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { FormikHelpers } from 'formik';

// Define the props for our custom component
interface ImageUploaderProps {
  name: string;
  label: string;
  logoUrl?: string | null;
  // Use a more specific type for setFieldValue
  setFieldValue: FormikHelpers<any>['setFieldValue'];
}

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(2, 0),
  },
  container: {
    border: '2px dashed',
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: 'center',
    minHeight: 150,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    maxHeight: 150,
    maxWidth: '100%',
    objectFit: 'contain',
  },
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

const ImageUploader: React.FC<ImageUploaderProps> = ({ name, label, logoUrl, setFieldValue }) => {
  const classes = useStyles();
  const [preview, setPreview] = useState<string | null>(logoUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // This is important for when the component is used in an "edit" mode
    // and the initial logoUrl prop changes.
    setPreview(logoUrl || null);
  }, [logoUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Set the File object in Formik's state
      setFieldValue(name, file);
      // Create a URL for local preview
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={classes.root}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {label}
      </Typography>
      <div className={classes.container}>
        {preview ? (
          <img src={preview} alt="Logo Preview" className={classes.image} />
        ) : (
          <div className={classes.placeholder}>
            <CloudUploadIcon fontSize="large" />
            <Typography variant="body2">No image selected</Typography>
          </div>
        )}
      </div>
      <Button
        variant="outlined"
        color="default"
        startIcon={<CloudUploadIcon />}
        onClick={handleButtonClick}
        className={classes.button}
      >
        {preview ? 'Change Image' : 'Select Image'}
      </Button>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept="image/*"
      />
    </div>
  );
};

export default ImageUploader;

